import React from 'react';
import { useField, FieldConfig } from 'formik';
import { Box, FormHelperText, Link, FormError, MultiComboboxProps, MultiCombobox } from 'pounceV2';
import { Link as RRLink } from 'react-router-dom';
import urls from 'Source/urls';
import useListAvailableDestinations from 'Hooks/useListAvailableDestinations';
import { safeguardErrorMsg } from 'Components/fields/utils';
import { SimpleRuleFormValues } from 'Pages/Build/Detections/Rules/SimpleRuleForm/SimpleRuleFormV3';

export type DestinationOverridesFieldProps = Partial<Omit<FieldConfig, 'name'>> &
  Partial<MultiComboboxProps<string>> &
  Partial<SimpleRuleFormValues>;

const DestinationOverridesFieldV3: React.FC<DestinationOverridesFieldProps> = props => {
  // @ts-ignore
  const [field, meta, { setValue }] = useField({ ...props, name: 'outputIds' } as FieldConfig);

  const {
    loading: destinationsLoading,
    destinationOutputIds: availableOutputIds,
    destinationIdToDisplayName: destIdToDisplayName,
    disabled: disableDestinationField,
    error: destinationsError,
  } = useListAvailableDestinations({ outputIds: field.value });

  const isInvalid = meta.touched && (meta.error || destinationsError);
  // if undefined, set it to true
  // *Combobox onChange does not return an event like Formik's field.onChange expects
  const handleChange = React.useCallback(
    value => {
      setValue(value);
      if (props.onChange) {
        props.onChange(value);
      }
    },
    // TODO: Update eslint because it should not be directing `props` to be a dep instead of `props.onChange`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onChange, setValue],
  );

  // Note that the field from formik contains a value key, and the value key is what
  // MultiCombobox looks for to render the currently selected items.
  // This gets set in the {...field} spreading.
  return (
    <Box>
      <MultiCombobox
        disabled={disableDestinationField || props.disabled}
        searchable
        label="Destination Overrides"
        items={availableOutputIds}
        itemToString={destIdToDisplayName}
        placeholder="Select destinations"
        aria-describedby="outputIds-description"
        {...props}
        {...field}
        onChange={handleChange}
      />
      {isInvalid ? (
        <FormError id="outputIds-description-error" mt={2}>
          {destinationsError
            ? 'There was a problem loading your destinations!'
            : safeguardErrorMsg(meta.error)}
        </FormError>
      ) : (
        <FormHelperText id="outputIds-description" mt={2} mr={1}>
          {destinationsLoading && 'Loading your destinations...'}
          {!destinationsLoading && !availableOutputIds.length && (
            <>
              You have not configured any destinations.
              <Link ml={1} as={RRLink} to={urls.configure.alertDestinations.create()} size="small">
                Create one
              </Link>
            </>
          )}
          {!destinationsLoading &&
            Boolean(availableOutputIds.length) &&
            'Send alerts to these destinations regardless of their severity level settings'}
        </FormHelperText>
      )}
    </Box>
  );
};

export default React.memo(DestinationOverridesFieldV3);
